import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { kTextClipCore } from "../../../Admin/kReusableCoreTS/kTextClipCore";
import { kDocChart, kDocChartSeries, kDocChartStyle, kDocChartSvg, kDocChartWeb } from "../../../Admin/kReusableCoreTS/kDocChartSvgCore";
import { kDocTheme } from "../../../Admin/kReusableCoreTS/kDocThemeCore";

// AppAssistChart - a CHART IN AN AI ANSWER, DRAWN BY THE SHARED CHART RENDERER FROM THE MODEL'S NUMBERS
// (owner-approved 2026-09-29: "Continue with the charts fix"; "If we have to build our own - use the shared code
// for it - we already have a lot of ready to use code for drawing charts").
//
// ==================================================================================================
// !! WHY
// ==================================================================================================
//
// Measured live 2026-09-28T06:41Z: the chip "Chart how we've been in touch" on a 4-message chat timed out
// ("first words 102733 ms, total 120217 ms"). The model was told to HAND-DRAW a whole `<svg>` - a coordinate per
// bar, a `<title>` per mark, axes, labels, quarter sums - and spent ~100 s on geometry before a word. Now it writes
// a small JSON spec in a ```wac-chart fence (`Fence`) - literally the shape of the shared `kDocChart`:
//   {"kind":"bar"|"stacked"|"line"|"barline", "title":"...", "categories":["2024 Q1",...],
//    "series":[{"name":"You","values":[12,...],"asLine":false}, ...], "unit":"messages"}
// and `kDocChartSvg` (kReusableCoreTS, DocumentBuilder's chart renderer) draws it with its web options
// (`kDocChartWeb`: colours by class, a `<title>` per mark, a summary `<title>`, whole numbers, thinned labels).
//
// ⛔ THE SPEC NEVER LEAVES THE SERVICE. `AssistRich` reads it (`Read`, strict), draws it (`Svg`) and cleans the
// drawing with the SAME shared allow-list as a model-written ```wac-html fence - so the answer, the stream's `rich`
// events, `ai-chat.json` and a kept pin carry only cleaned `<svg>` markup, and the page, Copy/Save chart, tap-for-
// value and "Keep this" work exactly as before (`AppAssistPanel.Rich` builds it node by node).
// ⛔ THE DRAWING PASSES THE ALLOW-LIST UNCHANGED (pinned by `AssistChart.test.ts`): colours ONLY through the classes
// `c1`..`c6` / `muted` (the CSP has no inline style, and the page paints them light and dark), no font-family.
// ⚠ Pure: no DOM, no `window`, no transport - the Bridge imports it like `AppAssistRich`.

/** A chart spec after `Read` accepted it: the shared chart data plus the words the panel says about it. */
export class AppAssistChartSpec {
    public Chart: kDocChart = new kDocChart();
    public Title: string = "";
    /** "messages" - said after a value in a title and the summary; the axis stays bare numbers. */
    public Unit: string = "";
}

export class AppAssistChart {
    /** The fence's info string: a line "```wac-chart" opens it, a line "```" closes it. */
    public static readonly Fence: string = "wac-chart";
    /** `kDocChart.Kind`s a panel chart may be (pie/donut need a legend beside them - not in a 320-wide panel). */
    public static readonly Kinds: string[] = ["bar", "stacked", "line", "barline"];
    public static readonly MaxLabels: number = 60;
    public static readonly MaxSeries: number = 6;
    public static readonly MaxTitle: number = 120;
    public static readonly MaxLabel: number = 24;
    public static readonly MaxName: number = 40;
    public static readonly MaxUnit: number = 20;
    public static readonly MaxValue: number = 1000000000;
    /** A spec longer than this is not read at all (sixty labels by six series fit in far less). */
    public static readonly MaxSpecChars: number = 12000;
    /** The policy's longest free-text attribute (`kHtmlSafePolicy.MaxTextChars`) - `aria-label` stays inside it. */
    public static readonly MaxAria: number = 200;
    /** Up to this many marks every one carries its value; above it the highest, the lowest and the last. */
    public static readonly LabelAll: number = 12;
    public static readonly Width: number = 320;
    public static readonly Height: number = 200;
    public static readonly TextSize: number = 10;
    /**
     * Control characters and the two Unicode line breaks. ⚠ A STRING for `new RegExp`, never a literal: the edit
     * tooling turns the U+2028 escape inside a regex literal into the raw character - a line break in the middle of
     * the expression ("Unterminated regular expression"; `AppAssistExport.Breaks`' lesson).
     */
    public static readonly Controls: RegExp = new RegExp("[\\u0000-\\u001f\\u007f\\u2028\\u2029]+", "g");

    // ── The reader ───────────────────────────────────────────────────────────────────────────

    /**
     * The spec in a fence's body, or null. ⛔ STRICT, EACH FIELD WITH ITS OWN `typeof`: a known kind, 1-60 categories
     * (non-empty text, cut to 24), 1-6 series (named, cut to 40) with EXACTLY one value per category - a finite number
     * from 0 to 1e9, or null for "no value" - a title (cut to 120) and an optional unit (cut to 20). Anything else,
     * including a spec over `MaxSpecChars`, is null - the service's ordinary "could not be shown" note. Unknown keys
     * are ignored (a model may add one); they never reach the drawing.
     */
    public static Read = (Body: string): AppAssistChartSpec | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Text: string = nz(Body).trim();
            if (Text.length === 0 || Text.length > AppAssistChart.MaxSpecChars) { return null; }
            const Raw: unknown = AppAssistChart.Parse(Text);
            if (Raw === null || typeof Raw !== "object" || Array.isArray(Raw) === true) { return null; }
            const Doc: Record<string, unknown> = Raw as Record<string, unknown>;
            if (typeof Doc.kind !== "string" || AppAssistChart.Kinds.indexOf(Doc.kind as string) < 0) { return null; }
            if (typeof Doc.title !== "string") { return null; }
            if (Doc.unit !== undefined && typeof Doc.unit !== "string") { return null; }
            const Out: AppAssistChartSpec = new AppAssistChartSpec();
            Out.Chart.Kind = Doc.kind as string;
            Out.Title = AppAssistChart.Words(Doc.title as string, AppAssistChart.MaxTitle);
            if (typeof Doc.unit === "string") { Out.Unit = AppAssistChart.Words(Doc.unit as string, AppAssistChart.MaxUnit); }
            const Categories: string[] | null = AppAssistChart.CategoriesIn(Doc.categories);
            if (Categories === null) { return null; }
            Out.Chart.Categories = Categories;
            const Series: kDocChartSeries[] | null = AppAssistChart.SeriesIn(Doc.series, Categories.length);
            if (Series === null) { return null; }
            Out.Chart.Series = Series;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistChart.Read");
            return null;
        }
    };

    /**
     * DOCUMENTED EXCEPTION: pure parse of model text in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: a model that wrote something other than JSON is an EXPECTED condition (fate 1) -
     * null becomes the "could not be shown" note; reporting it would latch the AI panel's scope over model output.
     */
    private static Parse = (Text: string): unknown => {
        try {
            return JSON.parse(Text);
        } catch (e) {
            return null;
        }
    };

    /** 1 to `MaxLabels` non-empty strings, each cut to `MaxLabel`; else null. */
    private static CategoriesIn = (Value: unknown): string[] | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Array.isArray(Value) === false) { return null; }
            const Rows: unknown[] = Value as unknown[];
            if (Rows.length === 0 || Rows.length > AppAssistChart.MaxLabels) { return null; }
            const Out: string[] = [];
            for (const One of Rows) {
                if (typeof One !== "string") { return null; }
                const Label: string = AppAssistChart.Words(One as string, AppAssistChart.MaxLabel);
                if (Label.length === 0) { return null; }
                Out.push(Label);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistChart.CategoriesIn");
            return null;
        }
    };

    /** 1 to `MaxSeries` series, each named with exactly `Count` values in range (and an optional boolean `asLine`). */
    private static SeriesIn = (Value: unknown, Count: number): kDocChartSeries[] | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Array.isArray(Value) === false) { return null; }
            const Rows: unknown[] = Value as unknown[];
            if (Rows.length === 0 || Rows.length > AppAssistChart.MaxSeries) { return null; }
            const Out: kDocChartSeries[] = [];
            for (const One of Rows) {
                if (One === null || typeof One !== "object" || Array.isArray(One) === true) { return null; }
                const Bag: Record<string, unknown> = One as Record<string, unknown>;
                if (typeof Bag.name !== "string" || Array.isArray(Bag.values) === false) { return null; }
                if (Bag.asLine !== undefined && typeof Bag.asLine !== "boolean") { return null; }
                const Series: kDocChartSeries = new kDocChartSeries();
                Series.Name = AppAssistChart.Words(Bag.name as string, AppAssistChart.MaxName);
                Series.AsLine = Bag.asLine === true;
                const Values: (number | null)[] | null = AppAssistChart.ValuesIn(Bag.values as unknown[], Count);
                if (Series.Name.length === 0 || Values === null) { return null; }
                Series.Values = Values;
                Out.push(Series);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistChart.SeriesIn");
            return null;
        }
    };

    /** Exactly `Count` values: finite numbers from 0 to `MaxValue`, or null (no value); else null. */
    private static ValuesIn = (Rows: unknown[], Count: number): (number | null)[] | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            if (Rows.length !== Count) { return null; }
            const Out: (number | null)[] = [];
            for (const One of Rows) {
                if (One === null) { Out.push(null); continue; }
                if (typeof One !== "number" || isFinite(One as number) === false) { return null; }
                if ((One as number) < 0 || (One as number) > AppAssistChart.MaxValue) { return null; }
                Out.push(One as number);
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistChart.ValuesIn");
            return null;
        }
    };

    /** One line of text, control characters and runs of space folded, cut to `Max` code points. */
    public static Words = (Text: string, Max: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const One: string = nz(Text).replace(AppAssistChart.Controls, " ").replace(/\s+/g, " ").trim();
            return kTextClipCore.ClipCodePoints(One, Max).trim();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistChart.Words");
            return "";
        }
    };

    // ── The drawing (the shared renderer's) ──────────────────────────────────────────────────────

    /**
     * The spec as ONE line of static `<svg>` markup, drawn by `kDocChartSvg` with the panel's web options - it passes
     * the shared allow-list unchanged (see the header). "" for no spec. `Labels` ("all" | "extremes" | "last" |
     * "none") overrides the panel's own choice (`LabelsFor`); "" keeps it.
     */
    public static Svg = (Spec: AppAssistChartSpec | null, Labels: string = ""): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            if (Spec === null) { return ""; }
            const One: AppAssistChartSpec = Spec as AppAssistChartSpec;
            const Chart: kDocChart = Object.assign(new kDocChart(), One.Chart);
            Chart.Unit = "";
            Chart.ValueLabels = AppAssistChart.LabelsFor(Chart);
            if (nz(Labels).length > 0) { Chart.ValueLabels = Labels; }
            return kDocChartSvg.Render(Chart, AppAssistChart.Theme(), "en-US", AppAssistChart.Width, AppAssistChart.Height,
                AppAssistChart.WebFor(One));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistChart.Svg");
            return "";
        }
    };

    /** The panel's web options: classes not hex, a `<title>` per mark, the summary, whole numbers, thinned labels. */
    public static WebFor = (Spec: AppAssistChartSpec): kDocChartWeb => {
        if (getCoreUtils().KErrorMode === true) { return new kDocChartWeb(); }
        try {
            const Out: kDocChartWeb = new kDocChartWeb();
            Out.Summary = AppAssistChart.SummaryOf(Spec);
            Out.AriaMax = AppAssistChart.MaxAria;
            Out.MarkTitles = true;
            if (Spec.Unit.length > 0) { Out.TitleUnit = " " + Spec.Unit; }
            Out.ClassColors = true;
            Out.InheritFont = true;
            Out.WholeNumbers = true;
            Out.ThinCategories = true;
            Out.TextEscape = true;
            Out.DotRadius = 3;
            Out.IntegerTicks = true;
            Out.KeepInside = true;
            // ⛔ A value label never reaches a neighbour's bar (2026-10-14: the Revenue chart's "3,200" over the bar beside it).
            Out.LabelsInColumn = true;
            // ⭐ Owner's choice (2026-10-14): grouped bars (the Revenue charts) carry their labels above the whole group.
            Out.LabelsAboveGroup = true;
            // Every series' own extremes get a label, and the axis makes room so none is left out (2026-10-14).
            Out.ExtremesBySeries = true;
            Out.LabelHeadroom = true;
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistChart.WebFor");
            return new kDocChartWeb();
        }
    };

    /**
     * The theme the panel draws with: a zero that reads on a web page ("0", not the document's dash), plain digit
     * grouping, and the text size for a 320-wide viewBox. ⚠ Colours never reach the markup (`ClassColors`).
     */
    public static Theme = (): kDocTheme => {
        if (getCoreUtils().KErrorMode === true) { return new kDocTheme(); }
        try {
            const Out: kDocTheme = new kDocTheme();
            Out.ZeroDisplay = "0";
            Out.ThinSpaceGrouping = false;
            const Style: kDocChartStyle = new kDocChartStyle();
            Style.Name = "wac-panel";
            Style.SizePt = AppAssistChart.TextSize;
            Out.ChartStyles = [Style];
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistChart.Theme");
            return new kDocTheme();
        }
    };

    /**
     * Value labels: "all" while there are `LabelAll` marks or fewer (side-by-side bars and lines count one mark per
     * series), else "extremes" - the highest, the lowest and the last.
     */
    public static LabelsFor = (Chart: kDocChart): string => {
        if (getCoreUtils().KErrorMode === true) { return "none"; }
        try {
            let Marks: number = Chart.Categories.length;
            if (Chart.Kind !== "stacked") { Marks = Marks * Math.max(1, Chart.Series.length); }
            if (Marks <= AppAssistChart.LabelAll) { return "all"; }
            return "extremes";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistChart.LabelsFor");
            return "none";
        }
    };

    /** The chart's summary: its title, then the highest point - "Messages per month. Highest: Aug 2024, 212 messages." */
    public static SummaryOf = (Spec: AppAssistChartSpec): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            let Title: string = Spec.Title;
            if (Title.length === 0) { Title = "Chart"; }
            let High: number = 0;
            for (let At: number = 1; At < Spec.Chart.Categories.length; At = At + 1) {
                if (AppAssistChart.HeightAt(Spec.Chart, At) > AppAssistChart.HeightAt(Spec.Chart, High)) { High = At; }
            }
            let Stop: string = ".";
            if (/[.!?]$/.test(Title) === true) { Stop = ""; }
            let Amount: string = (Math.round(AppAssistChart.HeightAt(Spec.Chart, High) * 100) / 100).toLocaleString("en-US");
            if (Spec.Unit.length > 0) { Amount = Amount + " " + Spec.Unit; }
            return Title + Stop + " Highest: " + Spec.Chart.Categories[High] + ", " + Amount + ".";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistChart.SummaryOf");
            return "";
        }
    };

    /** DOCUMENTED EXCEPTION: canonical height helper owns gates and this caller's fault label. */
    public static HeightAt = (Chart: kDocChart, At: number): number => {
        return kDocChartSvg.HeightOf(Chart, At, true, "AppAssistChart.HeightAt");
    };

    /**
     * The spec JSON for a chart the SERVICE builds itself (the instant "been in touch" chart) - so it goes through
     * exactly the model's path (`Read` -> `Svg` -> the allow-list). ⛔ Built with `JSON.stringify`, never by hand.
     */
    public static SpecText = (Kind: string, Title: string, Categories: string[], Series: kDocChartSeries[], Unit: string): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Rows: Record<string, unknown>[] = [];
            for (const One of Series) { Rows.push({ name: One.Name, values: One.Values.slice() }); }
            return JSON.stringify({ kind: Kind, title: Title, categories: Categories.slice(), series: Rows, unit: Unit });
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppAssistChart.SpecText");
            return "";
        }
    };
}
